import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  CircularProgress,
  Link,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import { makeStyles } from '@material-ui/core/styles';
import jwtDecode from 'jwt-decode';
import { appUrl } from '../../../../../../utils/config';
import { MOCK_LOGISTICS } from '../helpers/mockData';
import { SYNC_TRANSITIONS } from '../helpers/statusConfig';

const getApiBase = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

const syncedByName = () => {
  try {
    const t = jwtDecode(localStorage.getItem('authToken') || '');
    return t?.full_name || t?.email_address || 'System User';
  } catch (e) {
    return 'System User';
  }
};

// Renders a timestamp as two stacked lines — date over time — matching the design.
const DateTimeStacked = ({ value }) => {
  const dt = value instanceof Date ? value : new Date(value);
  if (!value || isNaN(dt.getTime())) return <span>—</span>;
  const date = `${String(dt.getDate()).padStart(2, '0')}-${MONTHS[dt.getMonth()]}-${dt.getFullYear()}`;
  const time = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}:${String(dt.getSeconds()).padStart(2, '0')}`;
  return (
    <>
      <div>{date}</div>
      <div>{time}</div>
    </>
  );
};

// Upload glyph (tray + up arrow) matching the drop-zone icon in the design.
const UploadGlyph = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#3a3f47"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 12,
    width: 640,
    maxWidth: '96vw',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    '& *': { fontFamily: 'inherit' },
  },
  content: {
    padding: '0 32px 30px',
    overflowX: 'hidden',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 14,
  },
  closeBtn: {
    textTransform: 'none',
    color: '#333',
    fontWeight: 500,
    fontSize: '0.9rem',
    minWidth: 0,
    padding: '2px 4px',
  },
  title: {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '1.5rem',
    color: '#1a1a1a',
    marginBottom: 22,
  },
  ribbon: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    backgroundColor: '#EEF4FB',
    border: '1px solid #DBE6F4',
    borderRadius: 8,
    padding: '16px 24px',
    marginBottom: 22,
  },
  ribbonItem: { display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 0', minWidth: 90 },
  ribbonLabel: { fontWeight: 700, fontSize: '0.8rem', color: '#222', lineHeight: 1.3 },
  ribbonValue: { fontWeight: 400, fontSize: '0.8rem', color: '#222', lineHeight: 1.3 },
  dropZone: {
    position: 'relative',
    border: '1px solid #E2E5EA',
    borderRadius: 8,
    padding: '26px 48px',
    textAlign: 'center',
    color: '#555',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    backgroundColor: '#FAFBFC',
    '&:hover': { borderColor: '#E75B25', backgroundColor: '#FFF8F5' },
  },
  dropIcon: {
    position: 'absolute',
    right: 22,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
  },
  dropText: { fontSize: '0.9rem' },
  fileInfoRow: {
    display: 'flex',
    gap: 90,
    marginBottom: 26,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  infoBlock: { display: 'flex', flexDirection: 'column', gap: 6 },
  infoLabel: { fontWeight: 700, fontSize: '0.8rem', color: '#222' },
  fileLink: {
    color: '#2B62C6',
    fontSize: '0.8rem',
    wordBreak: 'break-all',
    textDecoration: 'underline',
    cursor: 'pointer',
    textAlign: 'left',
  },
  actionWrap: { display: 'flex', justifyContent: 'center', marginTop: 8 },
  orangeBtn: {
    textTransform: 'none',
    borderRadius: 22,
    backgroundColor: '#E75B25',
    color: '#fff',
    padding: '8px 40px',
    fontSize: '0.9rem',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#C44A1A' },
    '&.Mui-disabled': { backgroundColor: '#E7B79F', color: '#fff' },
  },
  errorMsg: { color: '#A02828', fontSize: '0.82rem', textAlign: 'center', marginTop: 10 },
  // success
  statusChipsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 18,
    marginBottom: 22,
  },
  statusChip: {
    backgroundColor: '#2E9E2E',
    color: '#fff',
    borderRadius: 6,
    padding: '6px 18px',
    fontSize: '0.82rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  eventBox: {
    border: '1.5px solid #E12D2D',
    borderRadius: 8,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 20,
  },
  eventDotWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 3 },
  eventDot: { width: 14, height: 14, borderRadius: '50%', backgroundColor: '#E75B25', flexShrink: 0 },
  eventConnector: { width: 0, height: 22, borderLeft: '2px dotted #CBD2DA', marginTop: 3 },
  eventTitle: { fontWeight: 700, fontSize: '0.9rem', color: '#111' },
  eventTime: { fontSize: '0.82rem', color: '#333', lineHeight: 1.5 },
  metaRow: { display: 'flex', marginBottom: 8 },
  metaLabel: { fontWeight: 700, fontSize: '0.85rem', width: 110, color: '#111' },
  metaValue: {
    fontFamily: 'inherit',
    fontSize: '0.85rem',
    fontWeight: 400,
    lineHeight: 1.5,
    color: '#333',
    wordBreak: 'break-all',
    verticalAlign: 'baseline',
  },
}));

const SyncShipmentStatusDialog = ({ open, row, onClose }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | ingest | success
  const [file, setFile] = useState(null);
  const [uploadedAt, setUploadedAt] = useState(null);
  const [ingesting, setIngesting] = useState(false);
  const [error, setError] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep('upload');
      setFile(null);
      setUploadedAt(null);
      setIngesting(false);
      setError(false);
      setResult(null);
    }
  }, [open]);

  if (!row) return null;

  // Which one-step transition this Sync applies, chosen by the row's current status
  // (63 Shipment Scheduled → Shipped, or 64 Shipped → Goods Received). Falls back to
  // the Shipped → Goods Received transition for any unexpected status.
  const transition = SYNC_TRANSITIONS[row.statusID || row.statusId] || SYNC_TRANSITIONS[64];

  // Tell onClose whether the shipment was actually advanced — true only once the
  // success screen has been reached — so the dashboard can update the row's status.
  const closeDialog = () => onClose && onClose(step === 'success');

  const pickFile = () => fileInputRef.current && fileInputRef.current.click();

  const onFileChosen = (f) => {
    if (f) {
      setFile(f);
      setError(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    onFileChosen(f);
  };

  const handleUpload = () => {
    if (!file) return;
    setUploadedAt(new Date());
    setStep('ingest');
  };

  const handleIngest = async () => {
    setIngesting(true);
    setError(false);
    const doneAt = new Date();
    const success = () => {
      setResult({
        fileName: file ? file.name : '',
        deliveredAt: doneAt,
        syncedBy: syncedByName(),
      });
      setStep('success');
    };

    if (MOCK_LOGISTICS) {
      setTimeout(() => {
        success();
        setIngesting(false);
      }, 900);
      return;
    }

    try {
      const form = new FormData();
      if (file) form.append('file', file);
      form.append('sponsorShipmentID', row.sponsorShipmentID || '');
      form.append('collabrixFGBatchShipmentID', row.collabrixFGBatchShipmentID || '');
      form.append('vendorShipmentID', row.vendorShipmentID || '');
      form.append('freightForwarderScheduleID', row.freightForwarderScheduleID || '');
      // Backend fans out to Corda (and possibly SAP) which may be slow/unreachable
      // in some environments — bound the request so the overlay always clears.
      const res = await axios.post(`${getApiBase()}/syncShipmentStatus`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      if (res.data && (res.data.success || res.data.status)) {
        success();
      } else {
        setError(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[SyncShipmentStatus] Ingest failed', err);
      setError(true);
    } finally {
      setIngesting(false);
    }
  };

  const ribbonFields = [
    { label: 'Sponsor\nShipment ID',          value: row.sponsorShipmentID || '—' },
    { label: 'Freight Forwarder\nShipment ID', value: row.freightForwarderScheduleID || '—' },
    { label: 'Actual\nDispatch Date',          value: formatDate(row.actualDispatchDate || row.dispatchDate) },
    { label: 'Estimated\nArrival Date',        value: formatDate(row.estimatedArrivalDate) },
  ];

  const renderRibbon = () => (
    <div className={classes.ribbon}>
      {ribbonFields.map((f) => (
        <div key={f.label} className={classes.ribbonItem}>
          <Typography className={classes.ribbonLabel} style={{ whiteSpace: 'pre-line' }}>{f.label}</Typography>
          <Typography className={classes.ribbonValue}>{f.value}</Typography>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {ingesting && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999, gap: 16,
        }}>
          <CircularProgress size={52} style={{ color: '#fff' }} />
          <Typography style={{ color: '#fff', fontSize: '1rem', fontWeight: 500 }}>
            Syncing Shipment Status…
          </Typography>
        </div>
      )}

      <Dialog open={open} classes={{ paper: classes.paper }} maxWidth={false}>
        <DialogContent className={classes.content}>
          <div className={classes.topRow}>
            <Button
              className={classes.closeBtn}
              endIcon={<CloseIcon style={{ color: '#E53935', fontSize: 20 }} />}
              onClick={closeDialog}
            >
              Close
            </Button>
          </div>

          <Typography className={classes.title}>Sync Shipment Status</Typography>

          {step === 'success' ? (
            <>
              {/* Status chips + event now come from the SYNC_TRANSITIONS map so the
                  same dialog serves both flows. Old hardcoded values (Shipped →
                  Goods Received / "Goods Delivered at Consignee") kept for reference:
                  <span className={classes.statusChip}>Shipped</span>
                  <span className={classes.statusChip}>Goods Received</span>
                  <div className={classes.eventTitle}>Goods Delivered at Consignee</div> */}
              <div className={classes.statusChipsRow}>
                <span className={classes.statusChip}>{transition.fromLabel}</span>
                <ArrowRightAltIcon style={{ color: '#2E9E2E', fontSize: 34 }} />
                <span className={classes.statusChip}>{transition.toLabel}</span>
              </div>

              <div className={classes.eventBox}>
                <div className={classes.eventDotWrap}>
                  <div className={classes.eventDot} />
                  <div className={classes.eventConnector} />
                </div>
                <div>
                  <div className={classes.eventTitle}>{transition.eventTitle}</div>
                  <div className={classes.eventTime}>
                    <DateTimeStacked value={result?.deliveredAt} />
                  </div>
                </div>
              </div>

              <div style={{ display:'grid', justifyContent:'center'}}>
              <div className={classes.metaRow}>
                <span className={classes.metaLabel}>Source:</span>
                <span className={classes.metaValue}>Freight Forwarder XML</span>
              </div>
              <div className={classes.metaRow}>
                <span className={classes.metaLabel}>File Name:</span>
                <Link
                  component="button"
                  className={classes.metaValue}
                  style={{ color: '#2B62C6', textDecoration: 'underline' }}
                >
                  {result?.fileName}
                </Link>
              </div>
              <div className={classes.metaRow}>
                <span className={classes.metaLabel}>Synced by:</span>
                <span className={classes.metaValue}>{result?.syncedBy}</span>
              </div>
              </div>

              <div className={classes.actionWrap} style={{ marginTop: 22 }}>
                <Button className={classes.orangeBtn} onClick={closeDialog}>Ok</Button>
              </div>
            </>
          ) : (
            <>
              {renderRibbon()}

              {step === 'upload' ? (
                <>
                  <div
                    className={classes.dropZone}
                    onClick={pickFile}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                  >
                    <Typography className={classes.dropText}>
                      {file ? file.name : 'Drag & drop file or click here to upload files'}
                    </Typography>
                    <span className={classes.dropIcon}>
                      <UploadGlyph />
                    </span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => onFileChosen(e.target.files && e.target.files[0])}
                  />
                  <div className={classes.actionWrap}>
                    <Button className={classes.orangeBtn} onClick={handleUpload} disabled={!file}>
                      Upload
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.fileInfoRow}>
                    <div className={classes.infoBlock}>
                      <span className={classes.infoLabel}>File Name</span>
                      <Link component="button" className={classes.fileLink}>{file ? file.name : ''}</Link>
                    </div>
                    <div className={classes.infoBlock}>
                      <span className={classes.infoLabel}>Upload Timestamp</span>
                      <div style={{ fontSize: '0.8rem', color: '#222', lineHeight: 1.5 }}>
                        <DateTimeStacked value={uploadedAt} />
                      </div>
                    </div>
                  </div>

                  {error && (
                    <Typography className={classes.errorMsg}>
                      Something went wrong. Please try again.
                    </Typography>
                  )}

                  <div className={classes.actionWrap}>
                    <Button className={classes.orangeBtn} onClick={handleIngest} disabled={ingesting}>
                      Ingest
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SyncShipmentStatusDialog;
